<template>
	<div class="wrapper">
		<b-row>
			<b-col cols="12">
				<b-card header-tag="header">
					<template v-slot:header>
						<i class="fa fa-list"></i> Subscription Requests
					</template>

					<b-card-body>
						<!-- Office Filter & Export -->
						<b-row class="mb-7">
							<b-col cols="auto" class="mr-auto">
								<b-form-group label="Filter by Office" label-for="office-filter" :label-cols="4" :horizontal="true">
									<multiselect
										id="office-filter"
										v-model="selectedOffice"
										:options="officeList"
										:searchable="true"
										:allow-empty="true"
										label="name"
										track-by="id"
										placeholder="All Offices"
										@input="onOfficeChange()"
									/>
								</b-form-group>
							</b-col>
							<b-col cols="auto" class="mr-3">
								<b-form-group label="Sort By" label-for="sort-filter" :label-cols="3" :horizontal="true">
									<b-form-select
										id="sort-filter"
										v-model="sortDirection"
										:options="sortOptions"
										size="sm"
										@change="onSortChange()"
									></b-form-select>
								</b-form-group>
							</b-col>
							<b-col cols="auto">
								<b-button
									variant="primary"
									size="sm"
									:disabled="loading"
									@click="downloadExcel"
								>
									<i class="fa fa-download"></i> Export to Excel
								</b-button>
							</b-col>
						</b-row>

						<!-- Loading Spinner -->
						<div v-if="loading" class="text-center my-4">
							<b-spinner label="Loading..." style="width: 3rem; height: 3rem;" variant="info"></b-spinner>
						</div>

						<!-- Table -->
						<div v-else>
							<b-table
								:items="requestList"
								:fields="fields"
								hover
								striped
								bordered
								small
								responsive
								show-empty
								empty-text="No subscription requests found."
							>
								<!-- Office Name -->
								<template v-slot:cell(office)="row">
									{{ row.item.office ? row.item.office.name + ' ['+ row.item.office.id+']' : '—' }}
								</template>
								<template v-slot:cell(projectId)="row">
									{{ row.item.office.centralServerProjectId || '-' }}
								</template>
								<template v-slot:cell(location)="row">
									{{ row.item.location || '-' }}
								</template>
								<!-- Current Version -->
								<template v-slot:cell(current)="row">
									<b-badge :variant="badgeColor(row.item.currentVersion ? row.item.currentVersion.name : null)">{{ row.item.currentVersion ? row.item.currentVersion.name : '—' }}</b-badge>
								</template>

								<!-- Upgrade Version -->
								<template v-slot:cell(plan)="row">
									<b-badge :variant="badgeColor(row.item.upgradeVersion ? row.item.upgradeVersion.name : null)">{{ row.item.upgradeVersion ? row.item.upgradeVersion.name : '—' }}</b-badge>
								</template>


								<!-- Number of Employees -->
								<template v-slot:cell(numberOfEmployees)="row">
									{{ row.item.numberOfEmployees || '—' }}
								</template>

								<!-- Phone Number -->
								<template v-slot:cell(phoneNumber)="row">
									{{ row.item.phoneNumber || '—' }}
								</template>

								<template v-slot:cell(createdByName)="row">
									{{ row.item.createdByName || '-' }}
								</template>

								<template v-slot:cell([pageTitle])="row">
									{{ row.item.pageTitle || '-' }}
								</template>

								<template v-slot:cell(createdAt)="row">
									{{ row.item.createdAt ? formatDate(row.item.createdAt) : '-' }}
								</template>

								<template v-slot:cell(status)="row">
									<b-badge :variant="statusBadgeColor(row.item.status)">{{ statusBadge(row.item.status) }}</b-badge>
								</template>

							</b-table>

							<!-- Pagination -->
							<b-row class="mt-3">
								<b-col cols="6" class="d-flex align-items-center">
									<span class="text-muted small mr-3">Showing {{ paginationInfo }}</span>
									<b-form-group label="Per Page:" label-for="per-page-select" :label-cols="4" :horizontal="true" class="mb-0">
										<b-form-select
											id="per-page-select"
											v-model="perPage"
											:options="perPageOptions"
											size="sm"
											@change="onPerPageChange()"
										></b-form-select>
									</b-form-group>
								</b-col>
								<b-col cols="6">
									<b-pagination
										v-model="currentPage"
										:total-rows="totalRows"
										:per-page="perPage"
										align="right"
										size="sm"
										class="my-0"
										@change="onPageChange($event)"
									/>
								</b-col>
							</b-row>
						</div>
					</b-card-body>
				</b-card>
			</b-col>
		</b-row>
	</div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import toastService from '@/services/toastService';
import officeService from '@/services/officeService';
import subscriptionService from '@/services/subscriptionService';
import { formatDate } from '@/shared/dateUtils';

export default {
	name: 'SubscriptionRequestList',
	components: { Multiselect },
	data() {
		return {
			officeList: [],
			selectedOffice: null,
			requestList: [],
			loading: false,
			currentPage: 1,
			perPage: 10,
			totalRows: 0,
			sortDirection: 'asc',
			sortOptions: [
				{ value: 'asc', text: 'ID (Ascending)' },
				{ value: 'desc', text: 'ID (Descending)' }
			],
			perPageOptions: [
				{ value: 10, text: '10' },
				{ value: 25, text: '25' },
				{ value: 50, text: '50' },
				{ value: 100, text: '100' }
			],
			fields: [
				{ key: 'id',                label: 'ID',                     sortable: true  },
				{ key: 'office',            label: 'Office',                 sortable: false },
				{ key: 'projectId',         label: 'Project ID',             sortable: false },
				{ key: 'location',          label: 'Location',               sortable: false },
				{ key: 'current',    		  	label: 'Current Package',        sortable: false },
				{ key: 'plan',    				  label: 'Package Update Request', sortable: false },
				{ key: 'numberOfEmployees', label: 'No. of Employees',       sortable: false },
				{ key: 'phoneNumber',       label: 'Contact Number',         sortable: false },
				{ key: 'createdByName',     label: 'User Name',              sortable: false },
				{ key: 'pageTitle',         label: 'Page Title',              sortable: false },
				{ key: 'createdAt',         label: 'Request Time',           sortable: false },
				{ key: 'status',            label: 'Status',           sortable: false }
			],
		};
	},
	computed: {
		paginationInfo() {
			if (this.totalRows === 0) return '0 records';
			const start = (this.currentPage - 1) * this.perPage + 1;
			const end   = Math.min(this.currentPage * this.perPage, this.totalRows);
			return `${start}–${end} of ${this.totalRows}`;
		},
	},
	methods: {
		formatDate,
		loadRequests() {
			this.loading = true;
			const params = {
				page: this.currentPage - 1,
				per_page: this.perPage,
				sortById: this.sortDirection
			};
			if (this.selectedOffice) {
				params.officeId = this.selectedOffice.id;
			}
			subscriptionService.getSubscriptionRequests(
				params,
				(data) => {
					this.requestList = data.content || [];
					// Use totalElements if available, otherwise calculate from response
					this.totalRows = data.totalElements !== undefined ? data.totalElements : (data.content ? data.content.length : 0);
					this.loading = false;
				},
				(msg) => {
					toastService.showErrorToast(msg || 'Failed to load subscription requests');
					this.loading = false;
				}
			);
		},
		downloadExcel() {
			const params = {
				page: this.currentPage - 1,
				per_page: this.perPage,
				sortById: this.sortDirection
			};
			if (this.selectedOffice) {
				params.officeId = this.selectedOffice.id;
			}
			subscriptionService.exportSubscriptionRequestsToExcel(
				params,
				() => {
					toastService.showSuccessToast('Excel file downloaded successfully');
				},
				(msg) => {
					toastService.showErrorToast(msg || 'Failed to download Excel file');
				}
			);
		},
		badgeColor(status) {
			if(status === 'Essential') return 'success';
			if(status === 'Standard') return 'info';
			if(status === 'Premium') return 'secondary';
			return 'warning';
		},
		statusBadge(status) {
			if(status){
				return 'Pending';
			} else {
				return 'Processed';
			}
		},
		statusBadgeColor(status) {
			if(status){
				return 'warning';
			} else {
				return 'secondary';
			}
		},
		onOfficeChange() {
			this.currentPage = 1;
			this.loadRequests();
		},
		onPageChange(page) {
			// Bootstrap Vue emits the page number in the change event
			if (page !== undefined) {
				this.currentPage = page;
			}
			this.loadRequests();
		},
		onPerPageChange() {
			this.currentPage = 1;
			this.loadRequests();
		},
		onSortChange() {
			this.loadRequests();
		},
		statusLabel(status) {
			const map = { 0: 'Pending', 1: 'Active', 2: 'Expired', 3: 'Cancelled' };
			return map[status] !== undefined ? map[status] : String(status);
		},
		statusVariant(status) {
			const map = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'secondary' };
			return map[status] !== undefined ? map[status] : 'light';
		},
	},
	mounted() {
		officeService.getOfficeList(
			(list) => { this.officeList = list; },
			() => { toastService.showErrorToast('Failed to load office list'); }
		);
		this.loadRequests();
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
